import './flow-item.css';

import {
  IonButton,
  IonChip,
  IonIcon,
  IonLabel,
  IonReorder,
} from '@ionic/react';
import {
  addCircle,
  ellipsisHorizontal,
  informationCircle,
  play,
} from 'ionicons/icons';
import { DebouncedFunc, debounce } from 'lodash';
import React, {
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { BaseButton } from '../../../../../../components/button/base-button';
import { useFlowServices } from '../../../hook/flow-service.hooks';
import {
  ExecutionInfo,
  ExecutionInfoState,
} from '../../../hook/live-execution.hooks';
import { FlowItem, SystemUnit } from '../../../model/flow.types';
import { FlowItemIcon } from './flow-item-icon';
import { FlowItemPrecedingStem } from './flow-item-section';

export interface FlowItemBaseProps {
  flowItem: FlowItem;
  icon?: ReactNode;
  canAddItem?: boolean;
  canReorderItem?: boolean;
  onAddItemsBefore?: () => void;
  onAddItemsAfter?: () => void;
  onSelectItem?: () => void;
  isItemSelected?: boolean;
  isSectionSelected?: boolean;
  actions?: FlowItemAction[];
  highlight?: boolean;
  system?: SystemUnit;
  executionInfo?: ExecutionInfo;
  isStartItem?: boolean;
  isInactive?: boolean;
  onLongPress?: (e: Event, id: string) => void;
  presentItemQuickActions?: (e: Event, id: string) => void;
  label?: string;
  addPrecedingStem?: boolean;
}

export interface FlowItemAction {
  name: string;
  action: () => void;
}

const FlowItemBase = (props: FlowItemBaseProps): ReactElement => {
  const {
    actions,
    flowItem,
    highlight,
    canAddItem,
    icon,
    isItemSelected,
    isSectionSelected,
    canReorderItem,
    onAddItemsBefore,
    onAddItemsAfter,
    onSelectItem,
    system,
    executionInfo,
    isStartItem,
    isInactive,
    onLongPress,
    presentItemQuickActions,
    label,
    addPrecedingStem,
  } = props;

  const { flowItemService } = useFlowServices();

  const classList: string[] = [];
  if (isItemSelected === true || highlight) {
    classList.push('flow-item-selected');
  }
  if (canAddItem) {
    classList.push('add-action-visible');
  }
  if (onSelectItem) {
    classList.push('flow-item-row-clickable');
  }

  let messageColor;

  if (executionInfo) {
    const { state } = executionInfo;

    if (state === ExecutionInfoState.PLAYING) {
      classList.push('flow-item-executing-playing');
      messageColor = 'var(--ion-color-success)';
    } else if (state === ExecutionInfoState.PAUSED) {
      classList.push('flow-item-executing-paused');
      messageColor = 'var(--ion-color-warning)';
    } else {
      messageColor = 'var(--ion-color-danger)';
      classList.push('flow-item-executing-error');
    }
  }
  if (isStartItem) {
    classList.push('flow-item-start');
  }
  if (isInactive) {
    classList.push('flow-item-inactive');
  }

  const actionButtons = actions?.map((action, idx) => {
    const key = `${action.name.replace(/ /g, '-')}-${idx}`;
    return (
      <BaseButton key={key} buttonProps={{ onClick: action.action }}>
        {action.name}
      </BaseButton>
    );
  });

  const subheading: string | undefined = flowItemService.getFlowItemSubheading(
    flowItem,
    system
  );

  const longPressInvoked = useRef<boolean>(false);
  const isLongPressEnabled = onLongPress !== undefined;

  const handleIconClick = useCallback(
    (e: React.MouseEvent) => {
      if (isLongPressEnabled && longPressInvoked.current) {
        e.stopPropagation();
        e.preventDefault();
      }

      longPressInvoked.current = false;
    },
    [isLongPressEnabled]
  );

  const onClickRef = useRef<DebouncedFunc<() => void>>();

  const onClick = useCallback(() => {
    if (onSelectItem) {
      onSelectItem();
    }
  }, [onSelectItem]);

  useEffect(() => {
    onClickRef.current = debounce(onClick, 200);
  }, [onClick]);

  return (
    <div
      className={classList.join(' ')}
      data-cy={`flow-item-${props.flowItem?.ref?.replace(/ /g, '-')}`}
      onClick={() => {
        if (onClickRef.current) {
          onClickRef.current();
        }
      }}
      style={
        {
          '--flow-item-message-color': messageColor,
        } as CSSProperties
      }
    >
      {addPrecedingStem && (
        <FlowItemPrecedingStem
          canAddReorderItem={canAddItem}
          isSelected={isSectionSelected}
          onAddItemsAfter={() => {
            if (onAddItemsBefore) {
              onAddItemsBefore();
            }
          }}
        />
      )}
      <div className="machine-flow-item">
        <div className="machine-flow-item-icon-container">
          <FlowItemIcon
            onLongPress={
              onLongPress
                ? (e) => {
                    longPressInvoked.current = true;
                    if (flowItem.id) {
                      onLongPress(e, flowItem.id);
                    }
                  }
                : undefined
            }
            icon={icon}
            isItemSelected={isItemSelected}
            onClick={handleIconClick}
          />
          <StartAtIcon isStartItem={isStartItem} />
        </div>
        <div className="machine-flow-item-title">
          <IonLabel>
            {label ?? flowItem.name}
            {subheading && <p>{subheading}</p>}
          </IonLabel>
        </div>
        <div className="machine-flow-item-control-icon">
          {canReorderItem === true && (
            <IonButton color={'clear'} className="ion-no-padding">
              <IonReorder />
            </IonButton>
          )}
          {!canReorderItem && presentItemQuickActions && (
            <IonButton
              fill="clear"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (presentItemQuickActions && flowItem.id) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  presentItemQuickActions(e as any, flowItem.id);
                }
              }}
            >
              <IonIcon icon={ellipsisHorizontal} />
            </IonButton>
          )}
        </div>
        <div
          className="machine-flow-item-stem-container"
          onClick={(e) => {
            if (canAddItem && onAddItemsAfter) {
              e.stopPropagation();
              e.preventDefault();
              onAddItemsAfter();
            }
          }}
        >
          <div className="machine-flow-item-stem" />
          <div className={'machine-flow-item-add'}>
            <IonButton
              data-cy="flow-item-add"
              tabIndex={-1}
              style={{ outline: 'none' }}
              fill="clear"
            >
              <IonIcon icon={addCircle} color="success" />
            </IonButton>
          </div>
          <div className="machine-flow-item-stem" />
        </div>
        <div className="machine-flow-item-controls">{actionButtons}</div>
        {executionInfo?.message && (
          <div className="flow-item-execution-message-wrapper">
            <IonChip>
              {executionInfo?.message}
              <IonIcon color="text" icon={informationCircle} />
            </IonChip>
          </div>
        )}
      </div>
    </div>
  );
};

interface StartAtIconProps {
  isStartItem?: boolean;
}

const StartAtIcon = (props: StartAtIconProps): ReactElement | null => {
  const { isStartItem } = props;
  if (isStartItem) {
    return (
      <div className="start-at-icon-container">
        <IonIcon icon={play} />
      </div>
    );
  } else {
    return null;
  }
};

export { FlowItemBase };
